import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaPlus, FaCalendarAlt, FaTrash, FaEdit, FaSearch, FaFilter, FaCheckCircle, FaTimes, FaCalendar } from 'react-icons/fa';
import axios from 'axios';
import { useToast } from '../../context/ToastContext';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import EmailCampaignsNav from '../../components/ui/EmailCampaignsNav';
import StatCard, { StatGrid } from '../../components/ui/StatCard';
import FilterCard from '../../components/ui/FilterCard';
import DataTable, { TableEmpty } from '../../components/ui/DataTable';

const EventsManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    eventName: '',
    eventDate: '',
    category: 'custom',
    isRecurring: false,
    description: ''
  });
  const { success: toastSuccess, error: toastError } = useToast();

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setEvents(res.data.data);
      }
    } catch (err) {
      toastError('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        eventName: event.eventName,
        eventDate: event.eventDate.split('T')[0],
        category: event.category,
        isRecurring: event.isRecurring,
        description: event.description || ''
      });
    } else {
      setEditingEvent(null);
      setFormData({
        eventName: '',
        eventDate: '',
        category: 'custom',
        isRecurring: false,
        description: ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    try {
      if (editingEvent) {
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/events/${editingEvent._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toastSuccess('Event updated successfully');
      } else {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/events`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toastSuccess('Event created successfully');
      }
      setShowModal(false);
      fetchEvents();
    } catch (err) {
      toastError(err.response?.data?.message || 'Action failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toastSuccess('Event deleted');
      fetchEvents();
    } catch (err) {
      toastError('Delete failed');
    }
  };

  const filteredEvents = events.filter(e => 
    e.eventName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <PageLayout>
      <PageHeader
        title="Email Campaigns"
        description="Create and manage festival or deadline events"
        icon={FaCalendar}
        actions={
          <button onClick={() => handleOpenModal()} className="btn-premium flex items-center gap-2">
            <FaPlus className="w-4 h-4" /> Create Event
          </button>
        }
      />

      <EmailCampaignsNav />

      <StatGrid>
        <StatCard label="Total Events" value={events.length} icon={FaCalendarAlt} />
        <StatCard label="Recurring" value={events.filter(e => e.isRecurring).length} icon={FaCheckCircle} valueColor="text-primary-600" />
        <StatCard label="This Month" value={events.filter(e => new Date(e.eventDate).getMonth() === new Date().getMonth()).length} icon={FaCalendar} iconBg="bg-orange-100 dark:bg-orange-900/20" iconColor="text-secondary-600" valueColor="text-secondary-600" />
        <StatCard label="Categories" value={new Set(events.map(e => e.category)).size} icon={FaFilter} iconBg="bg-green-100 dark:bg-green-900/30" iconColor="text-green-600 dark:text-green-400" valueColor="text-green-600" />
      </StatGrid>

      <FilterCard>
        <div className="relative w-full">
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search events..."
            className="input-modern pl-11 py-2.5 text-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </FilterCard>

      {loading ? (
        <SkeletonLoader variant="table" rows={5} cols={5} />
      ) : (
        <DataTable>
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                <th className="px-4 py-4 text-left text-sm font-semibold">Event Name</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Date</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Category</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Recurring</th>
                <th className="px-4 py-4 text-right text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
              {filteredEvents.length === 0 ? (
                <TableEmpty colSpan={5} preset="create" title="No Events Yet" description="Create your first event to get started." />
              ) : filteredEvents.map(event => (
                <tr key={event._id}>
                  <td className="px-4 py-3 text-sm font-bold">{event.eventName}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <FaCalendarAlt className="text-primary-500" />
                      {new Date(event.eventDate).toLocaleDateString(undefined, { dateStyle: 'long' })}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="badge badge-info">{event.category}</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {event.isRecurring ? (
                      <span className="badge badge-success flex items-center gap-1 w-fit"><FaCheckCircle /> Yes</span>
                    ) : (
                      <span className="badge bg-gray-100 text-gray-500 dark:bg-dark-700 dark:text-gray-400">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-sm">
                    <div className="flex justify-end gap-2">
                      <button 
                        onClick={() => handleOpenModal(event)}
                        className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        onClick={() => handleDelete(event._id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTable>
      )}

      {showModal && createPortal(
        <div className="modal-overlay">
          <div className="modal-content !max-w-md">
            <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex justify-between items-center">
              <h2 className="text-xl font-bold dark:text-white">{editingEvent ? 'Edit Event' : 'Create New Event'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-gray-500">Event Name</label>
                <input 
                  required
                  type="text" 
                  className="input-modern" 
                  value={formData.eventName}
                  onChange={(e) => setFormData({...formData, eventName: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-gray-500">Event Date</label>
                  <input 
                    required
                    type="date" 
                    className="input-modern"
                    value={formData.eventDate}
                    onChange={(e) => setFormData({...formData, eventDate: e.target.value})}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-gray-500">Category</label>
                  <select 
                    className="input-modern"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                  >
                    <option value="festival">Festival</option>
                    <option value="tax-deadline">Tax Deadline</option>
                    <option value="promotional">Promotional</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2 py-2">
                <input 
                  type="checkbox" 
                  id="recurring"
                  className="w-4 h-4 text-primary-600 rounded"
                  checked={formData.isRecurring}
                  onChange={(e) => setFormData({...formData, isRecurring: e.target.checked})}
                />
                <label htmlFor="recurring" className="text-sm font-bold text-gray-700 dark:text-gray-300">Is this a recurring yearly event?</label>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-gray-500">Description</label>
                <textarea 
                  rows="3"
                  className="input-modern"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                ></textarea>
              </div>
              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-gray-200 dark:border-dark-600 rounded-lg font-bold text-gray-600">Cancel</button>
                <button type="submit" className="flex-1 btn-premium">{editingEvent ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </PageLayout>
  );
};

export default EventsManagement;
